import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartOptions,
    ChartData,
    TooltipItem,
    Plugin
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import {getTimeSeriesData, getAnnotationPeriods} from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        aggregation: string;
        total_count: number;
    };
}

interface SensorAnnotationPeriod {
    start: string;
    end: string;
    text: string;
}

interface TemperatureHumidityPressureChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    showStormyNoRain?: boolean;
    showTahmoQCHumiditySevere?: boolean;
}

const TemperatureHumidityPressureChart: React.FC<TemperatureHumidityPressureChartProps> = ({
                                                                                               stationId,
                                                                                               startDate,
                                                                                               endDate,
                                                                                               height = 400,
    showStormyNoRain = false,
    showTahmoQCHumiditySevere = false
                                                                                           }) => {
    const normalizeHumidityValue = (value: number | null) => {
        if (value === null || value === undefined) return value;
        return value <= 1 ? value * 100 : value;
    };

    // State management
    const [temperatureData, setTemperatureData] = useState<TimeSeriesData | null>(null);
    const [humidityData, setHumidityData] = useState<TimeSeriesData | null>(null);
    const [pressureData, setPressureData] = useState<TimeSeriesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
     const [stormyNoRainPeriods, setStormyNoRainPeriods] = useState<SensorAnnotationPeriod[]>([]);
    const [stormyNoRainPeriodsCache, setStormyNoRainPeriodsCache] = useState<SensorAnnotationPeriod[]>([]);
    const [humiditySeverePeriods, setHumiditySeverePeriods] = useState<SensorAnnotationPeriod[]>([]);
    const [humiditySeverePeriodsCache, setHumiditySeverePeriodsCache] = useState<SensorAnnotationPeriod[]>([]);
    const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

    const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

     const adjustedStartDate = useMemo(() =>
        moment(startDate).subtract(3, 'month').format('YYYY-MM-DDTHH:mm:ss'),
        [startDate]
    );

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch all three datasets in parallel
                const [tempResponse, humidityResponse, pressureResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'te', adjustedStartDate, endDate),
                    getTimeSeriesData(stationId, 'rh', adjustedStartDate, endDate),
                    getTimeSeriesData(stationId, 'ap', adjustedStartDate, endDate)
                ]);

                // Handle temperature data response
                if (tempResponse.status === 'fulfilled') {
                    setTemperatureData(tempResponse.value);
                } else {
                    console.error('Failed to fetch temperature data:', tempResponse.reason);
                }

                // Handle humidity data response
                if (humidityResponse.status === 'fulfilled') {
                    setHumidityData(humidityResponse.value);
                } else {
                    console.error('Failed to fetch humidity data:', humidityResponse.reason);
                }

                // Handle pressure data response
                if (pressureResponse.status === 'fulfilled') {
                    setPressureData(pressureResponse.value);
                } else {
                    console.error('Failed to fetch pressure data:', pressureResponse.reason);
                }

            } catch (err) {
                setError('Failed to fetch weather comparison data');
                console.error('Error fetching weather comparison data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, startDate, endDate]);

     useEffect(() => {
        const fetchStormyNoRainPeriods = async () => {
            try {
                const stormyNoRainResponse = await getAnnotationPeriods(stationId, 'stormy_no_rain', startDate, endDate);
                const periods = stormyNoRainResponse.periods || [];

                // Always cache the fetched data
                setStormyNoRainPeriodsCache(periods);

                // Only display if toggle is enabled
                if (showStormyNoRain) {
                    setStormyNoRainPeriods(periods);
                } else {
                    setStormyNoRainPeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch severe battery periods:', err);
                setStormyNoRainPeriodsCache([]);
                setStormyNoRainPeriods([]);
            }
        };

        fetchStormyNoRainPeriods();
    }, [stationId, startDate, endDate, showStormyNoRain]);

         // Effect to handle toggle changes using cached data
    useEffect(() => {
        if (showStormyNoRain) {
            setStormyNoRainPeriods(stormyNoRainPeriodsCache);
        } else {
            setStormyNoRainPeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showStormyNoRain, stormyNoRainPeriodsCache]);

      useEffect(() => {
        const fetchHumiditySeverePeriods = async () => {
            try {
                const fetchHumidityResponse = await getAnnotationPeriods(stationId, 'humidity_quality_tahmo_severe', startDate, endDate);
                const periods = fetchHumidityResponse.periods || [];

                // Always cache the fetched data
                setHumiditySeverePeriodsCache(periods);

                // Only display if toggle is enabled
                if (showTahmoQCHumiditySevere) {
                    setHumiditySeverePeriods(periods);
                } else {
                    setHumiditySeverePeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch severe battery periods:', err);
                setHumiditySeverePeriodsCache([]);
                setHumiditySeverePeriods([]);
            }
        };

        fetchHumiditySeverePeriods();
    }, [stationId, startDate, endDate, showTahmoQCHumiditySevere]);

               // Effect to handle toggle changes using cached data
    useEffect(() => {
        if (showTahmoQCHumiditySevere) {
            setHumiditySeverePeriods(humiditySeverePeriodsCache);
        } else {
            setHumiditySeverePeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showTahmoQCHumiditySevere, humiditySeverePeriodsCache]);


          // Create annotation plugin for battery issues
    const sensorAnnotationPlugin: Plugin<'line'> = useMemo(() => ({
        id: 'batteryAnnotation',
        beforeDraw: (chart: any) => {
            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

             // Helper function to draw text labels on periods
            const drawPeriodLabel = (startX: number, endX: number, text: string, color: string, yOffset: number = 0) => {
                const centerX = (startX + endX) / 2;
                const labelY = chartArea.top + 25 + yOffset;

                if (centerX >= chartArea.left && centerX <= chartArea.right) {
                    ctx.save();

                    // Measure text for background sizing
                    ctx.font = 'bold 12px Arial';
                    const textMetrics = ctx.measureText(text);
                    const textWidth = textMetrics.width;
                    const padding = 8;

                    // Draw background rectangle
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
                    ctx.fillRect(
                        centerX - textWidth/2 - padding,
                        labelY - 15,
                        textWidth + padding * 2,
                        20
                    );

                    // Draw border
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 2;
                    ctx.strokeRect(
                        centerX - textWidth/2 - padding,
                        labelY - 15,
                        textWidth + padding * 2,
                        20
                    );

                    // Draw text
                    ctx.fillStyle = color;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, centerX, labelY - 5);

                    ctx.restore();
                }
            };

            // Helper function to draw dotted vertical lines
            const drawDottedLine = (x: number, color: string) => {
                if (x >= chartArea.left && x <= chartArea.right) {
                    ctx.save();
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    ctx.beginPath();
                    ctx.moveTo(x, chartArea.top);
                    ctx.lineTo(x, chartArea.bottom);
                    ctx.stroke();
                    ctx.restore();
                }
            };


              // Draw stormy no rain(red)
            if (showStormyNoRain && stormyNoRainPeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(220, 53, 69, 0.1)';

                stormyNoRainPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );

                        // Draw period label
                        drawPeriodLabel(
                            Math.max(startX, chartArea.left),
                            Math.min(endX, chartArea.right),
                            'Stormy No Rain',
                            '#dc3545',
                            0
                        );

                    }
                });
                ctx.restore();

                 // Draw dotted lines for period boundaries
                stormyNoRainPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(220, 53, 69, 0.8)');
                    drawDottedLine(endX, 'rgba(220, 53, 69, 0.8)');
                });

            }

              // Draw Tahmo Humidity Severe
            if (showTahmoQCHumiditySevere && humiditySeverePeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(220, 53, 69, 0.1)';

                humiditySeverePeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );

                        // Draw period label
                        drawPeriodLabel(
                            Math.max(startX, chartArea.left),
                            Math.min(endX, chartArea.right),
                            'Severe Humidity QC',
                            '#dc3545',
                            0
                        );

                    }
                });
                ctx.restore();

                 // Draw dotted lines for period boundaries
                humiditySeverePeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(220, 53, 69, 0.8)');
                    drawDottedLine(endX, 'rgba(220, 53, 69, 0.8)');
                });

            }


        }
    }), [showStormyNoRain, stormyNoRainPeriods, showTahmoQCHumiditySevere, humiditySeverePeriods]);

    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
        const datasets: any[] = [];

        // Temperature dataset
        if (temperatureData && temperatureData.data && temperatureData.data.length > 0) {
            datasets.push({
                label: 'Temperature (°C)',
                data: temperatureData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: point.value
                    })),
                borderColor: '#ff6b6b',
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                spanGaps: false,
                tension: 0.1,
                yAxisID: 'y'
            });
        }

        // Humidity dataset
        if (humidityData && humidityData.data && humidityData.data.length > 0) {
            datasets.push({
                label: 'Humidity (%)',
                data: humidityData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: normalizeHumidityValue(point.value)
                    })),
                borderColor: '#4ecdc4',
                backgroundColor: 'rgba(78, 205, 196, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                spanGaps: false,
                tension: 0.1,
                yAxisID: 'y1'
            });
        }

        // Pressure dataset
        if (pressureData && pressureData.data && pressureData.data.length > 0) {
            datasets.push({
                label: 'Pressure (hPa)',
                data: pressureData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: point.value
                    })),
                borderColor: '#45b7d1',
                backgroundColor: 'rgba(69, 183, 209, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                spanGaps: false,
                tension: 0.1,
                yAxisID: 'y2'
            });
        }

        return {datasets};
    }, [temperatureData, humidityData, pressureData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Temperature, Humidity & Pressure',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    title: (context) => {
                        if (context.length > 0) {
                            return moment(context[0].parsed.x).format('DD MMM YYYY HH:mm');
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.parsed.y;
                        if (context.dataset.label?.includes('Humidity')) {
                            return `${context.dataset.label}: ${(value ?? 0).toFixed(2)}%`;
                        }
                        return `${context.dataset.label}: ${value?.toFixed(2) || 0}`;
                    }
                }
            },
            // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'HH:mm',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM/YY'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Temperature (°C)',
                    color: '#ff6b6b'
                },
                ticks: {
                    color: '#ff6b6b',
                    callback: function (value: any) {
                        return `${value}°C`;
                    }
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Humidity (%)',
                    color: '#4ecdc4'
                },
                ticks: {
                    color: '#4ecdc4',
                    callback: function (value: any) {
                        return `${value}%`;
                    }
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
            y2: {
                type: 'linear',
                display: true, // Hide third axis to avoid clutter, but data will still plot
                position: 'left',
                title: {
                    display: true,
                    text: 'Pressure (hPa)',
                    color: '#45b7d1'
                },
                ticks: {
                    color: '#45b7d1',
                    callback: function (value: any) {
                        return `${value} hPa`;
                    }
                },
                grid: {
                    drawOnChartArea: false,
                },
            }
        },
    }), []);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{height: `${height}px`}}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading temperature, humidity, and pressure data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    if (!temperatureData?.data?.length && !humidityData?.data?.length && !pressureData?.data?.length) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-warning">No weather data available for comparison</div>
                </div>
            </div>
        );
    }

    // Main render
    return (
        <div className="card">
             <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{height: `${height}px`}}>
                    <Chart
                        ref={chartRef}
                        key={chartKey}
                        type="line"
                        data={chartData}
                        options={chartOptions}
                        plugins={[sensorAnnotationPlugin]}
                    />
                </div>
                <div className="mt-2">
                    <small className="text-muted">
                        Temperature, Humidity & Pressure comparison over time
                        {temperatureData && temperatureData.data && temperatureData.data.length > 0 && (
                            <span className="ms-3">
                                Temperature: {temperatureData.data.filter(d => d.value !== null).length} points
                            </span>
                        )}
                        {humidityData && humidityData.data && humidityData.data.length > 0 && (
                            <span className="ms-3">
                                Humidity: {humidityData.data.filter(d => d.value !== null).length} points
                            </span>
                        )}
                        {pressureData && pressureData.data && pressureData.data.length > 0 && (
                            <span className="ms-3">
                                Pressure: {pressureData.data.filter(d => d.value !== null).length} points
                            </span>
                        )}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default TemperatureHumidityPressureChart;
